import { TMember } from "types/user.types";
import { instance } from "apis/interceptor";
import { TReservation } from "./meeting-reservation";
import { IReceiverMember } from "pages/meeting/complain-manager-setting/uis/table-view/person-table-view";
import { IAlarmChannel } from "apis/meeting-room-complain/alarm-channel";
import { TSlackChannel } from "apis/messangers";

export type TRestrictRequest = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  title: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  startDateTime: Date;
  endDateTime: Date;
  requestReason: string;
  resultReason: string;
  meetingRoom: {
    id: string;
    name: string;
    capacity: number;
    building: string;
    floor: string;
  };
  requestBy: TMember;
  adminBy: TMember;
};

export type TPaginationRejectResponse = {
  totalSearchNumber: number;
  totalPageNumber: number;
  nowPageNumber: number;
  blockRequests: TRestrictRequest[];
};

//* 내 회의실 예약 제한 요청 목록 조회
export const getMyRestrictRequestList = async (
  nowPageNumber: number
): Promise<{ success: boolean; data: TPaginationRejectResponse | null }> => {
  try {
    const res = await instance.post("/meetings/block-request/my/list", {
      nowPageNumber,
    });

    const formattingData = res.data.blockRequests.map((item: TRestrictRequest) => {
      return {
        ...item,
        createdAt: new Date(item.createdAt),
        updatedAt: new Date(item.updatedAt),
        startDateTime: new Date(item.startDateTime),
        endDateTime: new Date(item.endDateTime),
      };
    });

    const data = { ...res.data, blockRequests: formattingData };

    return { success: true, data };
  } catch (error) {
    return { success: false, data: null };
  }
};

//* 회의실 예약 제한 요청 생성
export const createRestrictRequest = async (
  meetingRoomId: string,
  title: string,
  startDateTime: Date,
  endDateTime: Date,
  requestReason: string
) => {
  try {
    const res = await instance.post("/meetings/block-request", {
      meetingRoomId,
      title,
      startDateTime,
      endDateTime,
      requestReason,
    });
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

//* 회의실 예약 제한 삭제
export const deleteRestrictRequest = async (id: string) => {
  try {
    const res = await instance.delete("/meetings/block-request", {
      data: {
        id: id,
      },
    });

    return { success: res.data.result };
  } catch (error) {
    return { success: false };
  }
};

//* 워크스페이스 내 블록 요청 조회 (관리자)
export const getWorkspaceRestrictRequestList = async (
  nowPageNumber: number,
  status: "PENDING" | "COMPLETED"
): Promise<{ success: boolean; data: TPaginationRejectResponse | null }> => {
  try {
    const res = await instance.post("/meetings/block-request/workspace/list", {
      nowPageNumber,
      status,
    });

    const formattingData = res.data.blockRequests.map((item: TRestrictRequest) => {
      return {
        ...item,
        createdAt: new Date(item.createdAt),
        updatedAt: new Date(item.updatedAt),
        startDateTime: new Date(item.startDateTime),
        endDateTime: new Date(item.endDateTime),
      };
    });

    const data = { ...res.data, blockRequests: formattingData };

    return { success: true, data };
  } catch (error) {
    return { success: false, data: null };
  }
};

//* 예약 제한과 충돌되는 예약 조회
export const getConflictReservationListByRestrictRequest = async (
  id: string
): Promise<{
  success: boolean;
  data: { meetingReservations: TReservation[]; meetingRestrictions: TReservation[] } | null;
}> => {
  try {
    const res = await instance.post("/meetings/block-request/existing-reservations", {
      id,
    });

    return { success: true, data: res.data };
  } catch (error) {
    return { success: false, data: null };
  }
};

//* 회의실 블록 요청 관리자 처리 (승인/거절)
export const processedStatusRestrictRequest = async (
  id: string,
  status: "APPROVED" | "REJECTED",
  resultReason: string
): Promise<{ success: boolean }> => {
  try {
    const res = await instance.patch("/meetings/block-request/admin", {
      id,
      status,
      resultReason,
    });
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

//* 블록 요청 알림 수신자 조회
export const getBlockRequestAlarmReceiverList = async (): Promise<{ success: boolean; data: IReceiverMember[] }> => {
  try {
    const res = await instance.get("/meetings/block-request/alarm-receiver");
    return { success: true, data: res.data };
  } catch (error) {
    return { success: false, data: [] };
  }
};

//* 블록 요청 알림 수신자 설정
export const setBlockRequestAlarmReceiver = async (memberList: TMember[]): Promise<{ success: boolean }> => {
  try {
    const res = await instance.post("/meetings/block-request/alarm-receiver", {
      memberList,
    });
    return { success: res.data.result };
  } catch (error) {
    return { success: false };
  }
};

//* 블록 요청 알림 수신자 삭제
export const deleteBlockRequestAlarmReceiver = async (id: string) => {
  try {
    const res = await instance.delete("/meetings/block-request/alarm-receiver", {
      data: {
        id,
      },
    });
    return { success: res.data.result };
  } catch (error) {
    return { success: false };
  }
};

//* 블록 요청 알림 슬랙 채널 조회
export const getBlockRequestAlarmSlackChannelList = async (): Promise<{
  success: boolean;
  data: IAlarmChannel[];
}> => {
  try {
    const res = await instance.get("/meetings/block-request/alarm-channel/slack");
    return { success: true, data: res.data };
  } catch (error) {
    return { success: false, data: [] };
  }
};

//* 블록 요청 알림 슬랙 채널 설정
export const setBlockRequestAlarmSlackChannelList = async (slackChannel: TSlackChannel) => {
  try {
    const res = await instance.post("/meetings/block-request/alarm-channel/slack", slackChannel);
    return { success: res.data.result };
  } catch (error) {
    return { success: false };
  }
};

//* 블록 요청 알림 슬랙 채널 삭제
export const deleteBlockRequestAlarmSlackChannel = async (id: string) => {
  try {
    const res = await instance.delete("/meetings/block-request/alarm-channel/slack", {
      data: {
        id,
      },
    });
    return { success: res.data.result };
  } catch (error) {
    return { success: false };
  }
};
